import {
  addIndex,
  any,
  append,
  defaultTo,
  endsWith,
  filter,
  find,
  forEachObjIndexed,
  groupBy,
  head,
  includes,
  isNil,
  last,
  length,
  map,
  mapObjIndexed,
  match,
  nth,
  path,
  pipe,
  prop,
  reject,
  split,
  values,
  assocPath,
  pathOr
} from "ramda";
import Lisatiedot from "../../lisatiedot";
import { __ } from "i18n-for-browser";
import {
  getRajoiteListamuodossa,
  getRajoitteetFromMaarays,
  isRajoiteVoimassa
} from "@/utils/rajoitteetUtils";
import { getAnchorPart } from "@/utils/anchor";
import { getVoimassaOlevatOppilasmaaraMaaraykset } from "@/helpers/muut";

/** ---------- Helpers: derive missing rajoiteId from anchors ---------- */

// Matches "rajoitteet_<uuid>" anywhere in anchor
const RAJOITE_ANCHOR_RX = /rajoitteet_([0-9a-fA-F-]{36})/;

const anchorToId = anchor => {
  if (typeof anchor !== "string") return null;
  const m = anchor.match(RAJOITE_ANCHOR_RX);
  return m?.[1] ?? null;
};

const getOrDeriveRajoiteId = m =>
  path(["meta", "rajoiteId"], m) ||
  pipe(
    pathOr([], ["meta", "changeObjects"]),
    map(path(["anchor"])),
    map(anchorToId),
    find(Boolean)
  )(m) ||
  null;

const ensureRajoiteId = m => {
  const id = getOrDeriveRajoiteId(m);
  return id ? assocPath(["meta", "rajoiteId"], id, m) : m;
};
/** -------------------------------------------------------------------- */

function isKokonaismaaraRajoite(rajoite) {
  return !!find(
    rajoite =>
      path(["properties", "metadata", "section"], rajoite) ===
        "opiskelijamaarat" &&
      path(["properties", "value", "value"], rajoite) === "kokonaismaara",
    rajoite.changeObjects
  );
}

export const previewOfOpiskelijamaarat = (
  { lomakedata, rajoitteet, maaraykset, rajoitepoistot },
  booleans,
  locale
) => {
  let structure = [];

  /** Derive + normalize rajoiteId for all maaraykset up front */
  const normalizedMaaraykset = map(ensureRajoiteId, maaraykset || []);

  /** Build a fast lookup of removed rajoiteIds */
  const removedIds = new Set(
    (rajoitepoistot || [])
      .map(x => path(["properties", "rajoiteId"], x))
      .filter(Boolean)
  );

  /** Kokonaismäärä rajoitteessa määräaika voi kohdistua koko rajoitteeseen tai vain sen alta
   * löytyvään kohdennukseen, joten käydään läpi nämä vaihtoehdot ja poistetaan ei voimassaolevat
   * rajoitteen osat tai koko rajoite **/
  const voimassaOlevatRajoitteet = reject(
    isNil,
    map(rajoite => {
      if (isKokonaismaaraRajoite(rajoite)) {
        const groups = groupBy(
          pipe(
            prop("anchor"),
            match(/^rajoitteet_[^.]+.kohdennukset.0.kohdennukset.[0-9]+/),
            head,
            defaultTo("-"),
            last
          ),
          rajoite?.changeObjects
        );

        // Tarkistetaan ensiksi päätaso, jos se ei ole voimassa ei tarvitse käsitellä rajoitetta pidemmälle
        if (!isRajoiteVoimassa({ changeObjects: groups["-"] })) {
          return null;
        }
        delete groups["-"];

        const voimassaOlevaRajoite = { ...rajoite };
        forEachObjIndexed((value, key) => {
          if (!isRajoiteVoimassa({ changeObjects: value })) {
            voimassaOlevaRajoite.changeObjects = filter(ch => {
              return !includes(
                `.kohdennukset.0.kohdennukset.${key}`,
                prop("anchor", ch)
              );
            }, voimassaOlevaRajoite?.changeObjects || []);
          }
        }, groups);
        return voimassaOlevaRajoite;
      }
      // Rajoite on yksittäinen opiskelijamäärä rajoite ja se ei sisällä kohdennuksia
      return isRajoiteVoimassa(rajoite) ? rajoite : null;
    }, rajoitteet || {})
  );

  const opiskelijamaararajoiteChangeObjsHtml = values(
    mapObjIndexed(rajoite => {
      return getRajoiteListamuodossa(
        rajoite.changeObjects,
        locale,
        nth(
          1,
          split(
            "_",
            getAnchorPart(
              path(["changeObjects", "0", "anchor"], rajoite || []),
              0
            )
          )
        )
      );
    }, voimassaOlevatRajoitteet || {})
  );

  /** Suodatetaan lisätietomääräys pois + filter out rajoitepoistot by rajoiteId */
  const voimassaOlevatMaaraykset = pipe(
    // Only opiskelijamäärä (kujalisamaareet)
    filter(m => m.koodisto === "kujalisamaareet"),
    // Compute "voimassa olevat" as before
    getVoimassaOlevatOppilasmaaraMaaraykset,
    // Filter out removed rajoiteIds (works whether id was original or derived)
    filter(m => {
      const id = getOrDeriveRajoiteId(m);
      if (id && removedIds.has(id)) return false;

      // If aliMaaraykset is a map keyed by rajoiteId, exclude if any key is removed
      const ali = m.aliMaaraykset;
      if (ali && !Array.isArray(ali)) {
        const aliKeys = Object.keys(ali);
        if (aliKeys.some(k => removedIds.has(k))) return false;
      }
      return true;
    })
  )(normalizedMaaraykset);

  const opiskelijamaararajoiteMaarayksetHtml = map(
    maarays =>
      getRajoitteetFromMaarays(
        maarays.aliMaaraykset,
        locale,
        __("rajoitteet.ajalla"),
        "tyyppi",
        true,
        maarays
      ),
    voimassaOlevatMaaraykset
  );

  const hasKokonaisopiskelijamaararajoitus =
    any(isKokonaismaaraRajoite, values(voimassaOlevatRajoitteet)) ||
    !!find(
      maarays => path(["meta", "tyyppi"], maarays) === "kokonaismaara",
      normalizedMaaraykset
    );

  if (
    length(values(voimassaOlevatRajoitteet)) > 0 ||
    length(normalizedMaaraykset) > 0
  ) {
    if (!hasKokonaisopiskelijamaararajoitus) {
      const eiKokonaisoppilasmaararajoitustaContent = {
        components: [
          {
            name: "HtmlContent",
            properties: {
              content: __("opiskelijamaara.kokonaismaaraEiRajattu")
            }
          }
        ]
      };
      structure = append(eiKokonaisoppilasmaararajoitustaContent, structure);
    }

    /** Lisätään määräyksistä luotu html struktuuriin */
    addIndex(map)((html, index) => {
      structure = append(
        {
          anchor: index,
          components: [
            {
              anchor: "opiskelijamaara",
              name: "HtmlContent",
              properties: {
                content: html
              }
            }
          ]
        },
        structure
      );
    }, opiskelijamaararajoiteMaarayksetHtml);

    /** Lisätään muutos-objekteista luotu html struktuuriin */
    addIndex(map)((html, index) => {
      structure = append(
        {
          anchor: index,
          components: [
            {
              anchor: "opiskelijamaara",
              name: "HtmlContent",
              properties: {
                content: html
              }
            }
          ]
        },
        structure
      );
    }, opiskelijamaararajoiteChangeObjsHtml);
  }

  const lisatiedotNode = find(
    // 1 = koodiston koodiarvo
    node => endsWith(".lisatiedot.1", node.anchor),
    lomakedata
  );

  if (lisatiedotNode && lisatiedotNode.properties.value) {
    structure = append(Lisatiedot(lisatiedotNode.properties.value), structure);
  }

  return structure;
};
